import { useSelector, useDispatch } from 'react-redux';
import {
    setTransferUnitForRoute,
    clearTransferUnitForRoute,
    setTransferSearchResults,
    setTransferRouteDistance,
    searchTransfers,
} from 'src/store/reservations/slices/transfers/ManageTransferSlice';
import { formatDateToString } from 'src/store/reservations/actions/transfers/TransferRouteCalculator';
import ApiClientConnector from 'src/api/ApiClientConnector';
import { useMsal } from '@azure/msal-react';
import { fetchLocationsDistance } from 'src/store/location/LocationManagerSlice';
import { defaultTransferPassengers } from 'src/utils/constants';
import { parseDistanceKm } from 'src/store/reservations/actions/transfers/TransferCostCalculator';

const formatDistance = (km) => `${Number(km).toFixed(2)} km`;
const roundTransferValue = (value) => Number(Number(value || 0).toFixed(2));

const formatDuration = (hrs) => {
    const totalMinutes = Math.round(Number(hrs) * 60);
    return totalMinutes >= 60
        ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
        : `${totalMinutes}m`;
};

export const useFetchRouteDistances = () => {
    const dispatch = useDispatch();
    const { instance, accounts } = useMsal();
    const transferRoutes = useSelector(state => state.transferReducer.transferRoutes);

    return async (routesToFetch = null) => {
        const routesToProcess = routesToFetch ?? transferRoutes;
        if (!routesToProcess.length) return;

        try {
            const distanceRequestPerRoute = routesToProcess.map(transferRoute => ({
                From: transferRoute.fromLocation.locationId,
                To: transferRoute.toLocation.locationId,
                WayPoints: (transferRoute.waypoints ?? []).map(waypoint => ({
                    LocationId: waypoint.locationId,
                })),
            }));

            const distanceResultsPerRoute = await dispatch(
                fetchLocationsDistance(instance, accounts, distanceRequestPerRoute)
            );

            routesToProcess.forEach((transferRoute, routeIndex) => {
                const routeDistanceResult = distanceResultsPerRoute?.[routeIndex];
                if (!routeDistanceResult?.Distances?.length) return;

                const roadSegments = routeDistanceResult.Distances;
                const isDayTripRoute = transferRoute.fromLocation.locationId === transferRoute.toLocation.locationId;

                let routeTotalDistanceKm;
                let routeTotalDurationHrs;
                let perWaypointDistanceLabels = [];

                if (isDayTripRoute) {
                    routeTotalDistanceKm = roadSegments.reduce((sum, seg) => sum + Number(seg.Distance), 0);
                    routeTotalDurationHrs = roadSegments.reduce((sum, seg) => sum + Number(seg.Duration), 0);
                    const outboundSegments = roadSegments.slice(0, roadSegments.length - 1);
                    perWaypointDistanceLabels = outboundSegments.map(seg => formatDistance(seg.Distance));

                } else if (roadSegments.length === 1) {
                    routeTotalDistanceKm = Number(roadSegments[0].Distance);
                    routeTotalDurationHrs = Number(roadSegments[0].Duration);

                } else {
                    routeTotalDistanceKm = roadSegments.reduce((sum, seg) => sum + Number(seg.Distance), 0);
                    routeTotalDurationHrs = roadSegments.reduce((sum, seg) => sum + Number(seg.Duration), 0);
                    const waypointSegments = roadSegments.slice(0, roadSegments.length - 1);
                    perWaypointDistanceLabels = waypointSegments.map(seg => formatDistance(seg.Distance));
                }

                dispatch(setTransferRouteDistance({
                    routeId: transferRoute.routeId,
                    distance: formatDistance(routeTotalDistanceKm),
                    duration: formatDuration(routeTotalDurationHrs),
                    waypointDistances: perWaypointDistanceLabels,
                }));
            });

        } catch (error) {
            console.error('Failed to fetch route distances:', error);
        }
    };
};

export const useTransferRoute = (routeId) => {
    const dispatch = useDispatch();
    const { instance, accounts } = useMsal();

    const route = useSelector(state =>
        state.transferReducer.transferRoutes.find(r => r.routeId === routeId)
    );

    const selectedUnit = useSelector(
        state => state.transferReducer.transferUnits[routeId]
    );

    const searchTransfers = async () => {
        if (!route) return [];
        try {
            const client = await ApiClientConnector(instance, accounts);
            const results = await client.transfers.search({
                FromLocationId: route.fromLocation.locationId,
                ToLocationId: route.toLocation.locationId,
                FromDate: route.fromDate,
                ToDate: route.toDate,
            });
            dispatch(setTransferSearchResults({ routeId, results }));
            return results;
        } catch (error) {
            console.error('Transfer search failed for route', routeId, error);
            return [];
        }
    };

    const selectUnit = (unit) => dispatch(setTransferUnitForRoute({ routeId, unit }));
    const clearUnit = () => dispatch(clearTransferUnitForRoute({ routeId }));

    return { route, selectedUnit, searchTransfers, selectUnit, clearUnit };
};

export const buildTransferReservationPayload = (
    transferRoutes = [],
    transferUnits = {},
    transferPassengers = defaultTransferPassengers
) => {

    const selectedRoutes = transferRoutes
        .filter(route => transferUnits[route.routeId])
        .map(route => ({
            route,
            selectedUnit: transferUnits[route.routeId],
        }));

    if (!selectedRoutes.length) {
        return [];
    }

    const groupedTransfers = new Map();

    selectedRoutes.forEach(({ route, selectedUnit }) => {
        const groupKey = `${selectedUnit?.UnitTypeId ?? ''}|${selectedUnit?.ContractId ?? ''}`;
        const routeDistance = roundTransferValue(parseDistanceKm(route.distance));

        if (!groupedTransfers.has(groupKey)) {
            groupedTransfers.set(groupKey, {
                unitTypeId: selectedUnit?.UnitTypeId ?? null,
                contractId: selectedUnit?.ContractId ?? null,
                totalCost: roundTransferValue(selectedUnit?.Cost),
                adults: transferPassengers?.adults ?? defaultTransferPassengers.adults,
                children: Array.isArray(transferPassengers?.children) ? transferPassengers.children : [],
                routes: [],
                totalDistance: 0,
            });
        }

        const group = groupedTransfers.get(groupKey);
        group.routes.push({ route, routeDistance });
        group.totalDistance += routeDistance;
    });

    return Array.from(groupedTransfers.values()).map(group => {
        const totalDistance = roundTransferValue(group.totalDistance);
        let allocatedCost = 0;

        const units = group.routes.map(({ route, routeDistance }, routeIndex) => {
            const isLastRoute = routeIndex === group.routes.length - 1;
            const routeCost = isLastRoute
                ? roundTransferValue(group.totalCost - allocatedCost)
                : roundTransferValue(totalDistance > 0 ? (group.totalCost * routeDistance) / totalDistance : 0);

            allocatedCost += routeCost;

            return {
                From: route.fromLocation.locationId,
                To: route.toLocation.locationId,
                Distance: routeDistance,
                CheckIn: formatDateToString(route.fromDate),
                CheckOut: formatDateToString(route.toDate),
                ClientSellTotal: routeCost,
                WayPoints: (route.waypoints ?? []).map(waypoint => ({
                    LocationId: waypoint.locationId,
                    Name: waypoint.name,
                })),
            };
        });

        return {
            UnitTypeId: group.unitTypeId,
            ContractId: group.contractId,
            TotalCost: roundTransferValue(units.reduce((sum, unit) => sum + unit.ClientSellTotal, 0)),
            TotalDistance: totalDistance.toFixed(2),
            Adults: group.adults,
            Children: group.children,
            Units: units,
        };
    });
};

export const useSearchTransfers = () => {
    const { instance, accounts } = useMsal();
    const dispatch = useDispatch();
    const transferRoutes = useSelector(state => state.transferReducer.transferRoutes);

    return async ({ adults, children } = {}) => {
        if (!transferRoutes.length) return [];

        const firstRoute = transferRoutes[0];

        const payload = {
            CheckIn: firstRoute.fromDate,
            CheckOut: firstRoute.toDate,
            Transfer: {
                FromLocationId: firstRoute.fromLocation.locationId,
                ToLocationId: firstRoute.toLocation.locationId,
                Units: [
                    {
                        Adults: adults ?? 2,
                        Children: Array.isArray(children) ? children : [],
                    }
                ],
            },
            TransferServiceUnitId: null,
        };

        const response = await dispatch(searchTransfers(instance, accounts, payload));
        return response ?? [];
    };
};

export const getPassengerProfileFromItem = (item) => {
    const occupants = Array.isArray(item?.Rooms)
        ? item.Rooms
        : Array.isArray(item?.Units)
            ? item.Units
            : [];

    const adults = occupants.reduce((total, occupant) => total + (occupant.Adults || 0), 0);
    const children = occupants.flatMap(occupant => Array.isArray(occupant.Children) ? occupant.Children : []);

    return {
        adults,
        children,
        passengerCount: adults + children.length,
        sortDate: item?.CheckIn || item?.CheckOut || '',
    };
};

export const deriveTransferPassengersFromReservation = (hotels = [], services = []) => {
    const hotelProfiles = hotels.map(getPassengerProfileFromItem).filter(profile => profile.passengerCount > 0);
    const serviceProfiles = services.map(getPassengerProfileFromItem).filter(profile => profile.passengerCount > 0);
    const profiles = hotelProfiles.length > 0 ? hotelProfiles : serviceProfiles;

    if (profiles.length === 0) {
        return defaultTransferPassengers;
    }

    const [selectedProfile] = [...profiles].sort((left, right) => {
        if (right.passengerCount !== left.passengerCount) {
            return right.passengerCount - left.passengerCount;
        }

        return new Date(left.sortDate).getTime() - new Date(right.sortDate).getTime();
    });

    return {
        adults: selectedProfile.adults,
        children: selectedProfile.children,
    };
};